/**
 * TranscriptViewer Component
 *
 * Display transcript with diarization (speaker segments)
 *
 * Features:
 * - Speaker labels (agent/client)
 * - Timestamps for each segment
 * - Highlight current segment during playback
 * - Click segment to seek audio
 * - Confidence scores
 * - Right-click context menu for annotations
 * - Display annotations as highlights
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pin, Play } from 'lucide-react';
import { transcriptCharOffsets } from '@aica/core';
import { locateQuote } from '@aica/core/voc';
import { cn } from '@/lib/utils';
import type { SpeakerRoleMap } from '@/lib/speaker-roles';
import { speakerLabel, speakerNamesFor, speakerSide } from '@/lib/transcript-speaker';
import {
  applyDisplayEdits,
  displayPointFromEdits,
  expandRangeOverMarkers,
  hasQuotedText,
  quotedRegions,
  toSourcePoint,
  transcriptDisplayEdits,
} from '@aica/core';

export interface DiarizationSegment {
  speaker: string;
  start: number; // milliseconds
  end: number; // milliseconds
  text: string;
  confidence?: number;
  /** The segment's own role, when stored; wins over the label's mapped side. */
  role?: string;
}

export interface Annotation {
  id: string;
  analysisId: string;
  recordId: string;
  createdByUserId: string;
  type: 'objection_handling' | 'discovery_gap' | 'tone_issue' | 'other';
  severity: 'low' | 'medium' | 'high';
  note: string;
  startPosition: number | null;
  endPosition: number | null;
  createdAt: string;
  updatedAt: string;
  /** The reader may edit and delete it — set by the list read (#2915). */
  canEdit?: boolean;
}

export interface TranscriptViewerProps {
  segments: DiarizationSegment[];
  /**
   * Conversation role per diarization label, as resolved by the analysis
   * pipeline. Needed because the label itself carries no role whenever the
   * transcript is the protected ASR source (`speaker_0`, `speaker_1`).
   */
  speakerRoles?: SpeakerRoleMap;
  currentTime?: number; // seconds
  onSeek?: (timeInSeconds: number) => void;
  className?: string;
  analysisId?: string; // For annotations
  annotations?: Annotation[]; // Annotations to display
  onAnnotationCreate?: (
    selectedText: string,
    startPosition: number,
    endPosition: number
  ) => void;
  /**
   * Hide hesitation sounds («е-е-е», «ммм») from the shown text. Calls only:
   * a chat is text a person typed. The stored text stays verbatim — analysis,
   * forbidden words and speaking pace read it (#819).
   */
  stripHesitations?: boolean;
  /**
   * Names from the record, so a chat line reads «Олена», not `agent (17732)`
   * (#889). The operator is recognised by `agentId`; see `speakerLabel`.
   */
  agentId?: string | null;
  agentName?: string | null;
  participantName?: string | null;
  /**
   * A client quote to mark and scroll to - the objection the reader came from
   * (#846). Found by text: the analysis stores no turn number for objections.
   */
  highlightQuote?: string;
}

const EMPTY_ANNOTATIONS: Annotation[] = [];

// Get severity color for annotation highlights — prototype-matching styles
// high = danger (problem), medium = warning, low = success (positive)
function getHighlightSeverityColor(severity: 'low' | 'medium' | 'high') {
  switch (severity) {
    case 'low':
      return 'bg-success-dim text-success-fg border-l-2 border-success';
    case 'medium':
      return 'bg-warning-dim text-warning-fg border-l-2 border-warning';
    case 'high':
      return 'bg-danger-dim text-danger-fg border-l-2 border-danger';
    default:
      return 'bg-fg-subtle/12 text-fg-subtle border-l-2 border-fg-subtle';
  }
}

interface SegmentTextProps {
  text: string;
  highlights: Array<{
    annotation: Annotation;
    startInSegment: number;
    endInSegment: number;
  }>;
  /** Surface language for the attachment marker (#476). */
  language: string;
  /** Hide hesitation sounds — calls only (#819). */
  stripHesitations: boolean;
}

function SegmentText({ text, highlights, language, stripHesitations }: SegmentTextProps) {
  // Display-only: the stored text stays verbatim, and annotations are anchored
  // by absolute character offsets over it (see charOffsets). The shown text is
  // rendered ONCE for the whole segment and every highlight is translated into
  // its coordinates — slicing the stored text first and rendering each chunk
  // would split a hidden sound at a chunk edge and shift what follows.
  // Правки считаются ОДИН раз на реплику: их же переводят границы подсветок.
  const edits = transcriptDisplayEdits(text, language, { stripHesitations });
  const shown = applyDisplayEdits(text, edits);
  // Цитата внутри реплики — чужие слова (#889): показываются приглушённо, в тех
  // же координатах показа, что и подсветка.
  // Звонок цитат не несёт: без `[quote]` регулярка не нужна вовсе — реплика
  // перерисовывается на каждом тике проигрывания.
  const quotes = hasQuotedText(text)
    ? quotedRegions(text).map((region) => ({
        start: displayPointFromEdits(edits, region.start, 'start'),
        end: displayPointFromEdits(edits, region.end, 'end'),
      }))
    : [];

  if (highlights.length === 0 && quotes.length === 0) {
    return <span>{shown}</span>;
  }

  const parts: Array<{ from: number; to: number; annotation?: Annotation }> = [];
  let lastIndex = 0;

  const sortedHighlights = [...highlights].sort(
    (a, b) => a.startInSegment - b.startInSegment
  );

  sortedHighlights.forEach((highlight) => {
    const start = displayPointFromEdits(edits, highlight.startInSegment, 'start');
    const end = displayPointFromEdits(edits, highlight.endInSegment, 'end');
    if (start > lastIndex) {
      parts.push({ from: lastIndex, to: start });
    }
    // Обе границы не левее уже отрисованного и не левее друг друга: у
    // `substring` аргументы «наоборот» меняются местами, и подсветка получала
    // бы чужой текст на аннотациях, пересекающихся в исходных координатах.
    const from = Math.max(start, lastIndex);
    parts.push({ from, to: Math.max(from, end), annotation: highlight.annotation });
    lastIndex = Math.max(lastIndex, end);
  });

  if (lastIndex < shown.length) {
    parts.push({ from: lastIndex, to: shown.length });
  }

  // Цитата приглушена и внутри аннотации: фон и полоса аннотации остаются, а
  // чужие слова не сливаются со своими (Codex на #1134). Без цитаты — один
  // текстовый узел, как было.
  const content = (from: number, to: number) => {
    const pieces = splitByQuotes(from, to, quotes);
    if (!pieces.some((piece) => piece.quoted)) return shown.substring(from, to);
    return pieces.map((piece) => (
      <span
        key={piece.from}
        className={piece.quoted ? 'text-fg-muted' : undefined}
        data-quote={piece.quoted ? '' : undefined}
      >
        {shown.substring(piece.from, piece.to)}
      </span>
    ));
  };

  return (
    <span>
      {/* Ключ — порядковый номер куска: смещения совпадают у двух аннотаций,
          целиком попавших в скрытые звуки, и React получал дубли. */}
      {parts.map((part, index) => {
        if (part.annotation) {
          const severityColor = getHighlightSeverityColor(part.annotation.severity);
          return (
            <span
              key={`ann-${part.annotation.id}`}
              className={cn('rounded-r-sm px-2 py-0.5', severityColor, 'cursor-help')}
              title={`${part.annotation.type} (${part.annotation.severity}): ${part.annotation.note}`}
            >
              {content(part.from, part.to)}
            </span>
          );
        }
        return <span key={`text-${index}`}>{content(part.from, part.to)}</span>;
      })}
    </span>
  );
}

/** Куски `[from, to)`, помеченные тем, лежат ли они внутри цитаты. */
function splitByQuotes(
  from: number,
  to: number,
  quotes: Array<{ start: number; end: number }>
): Array<{ from: number; to: number; quoted: boolean }> {
  const pieces: Array<{ from: number; to: number; quoted: boolean }> = [];
  let cursor = from;
  for (const quote of quotes) {
    const start = Math.max(quote.start, cursor);
    const end = Math.min(quote.end, to);
    if (start >= end) continue;
    if (start > cursor) pieces.push({ from: cursor, to: start, quoted: false });
    pieces.push({ from: start, to: end, quoted: true });
    cursor = end;
  }
  if (cursor < to) pieces.push({ from: cursor, to, quoted: false });
  return pieces;
}

export function TranscriptViewer({
  segments,
  speakerRoles,
  currentTime = 0,
  onSeek,
  className,
  analysisId,
  annotations = EMPTY_ANNOTATIONS,
  onAnnotationCreate,
  stripHesitations = false,
  agentId,
  agentName,
  participantName,
  highlightQuote,
}: TranscriptViewerProps) {
  const { t, i18n } = useTranslation('records');
  const activeSegmentRef = useRef<HTMLDivElement>(null);
  // The side of each label, as the rest of the viewer resolves it: a manager
  // repeating the customer's words must not be taken for the quote (the
  // Objections screen's excerpt skips agent turns the same way, #846).
  const quoteRange = useMemo(
    () =>
      highlightQuote
        ? locateQuote(
            segments.map((segment) => ({
              speaker: segment.speaker,
              text: segment.text,
              role:
                segment.role && segment.role !== 'unknown'
                  ? segment.role
                  : (speakerSide(segment.speaker, speakerRoles) ?? undefined),
            })),
            highlightQuote
          )
        : null,
    [segments, speakerRoles, highlightQuote]
  );
  // A string, not the range object: a refetched transcript yields a new object
  // for the same quote, and the view must not jump back to it while read.
  const quoteKey = quoteRange ? `${quoteRange.from}:${quoteRange.to}` : null;
  const [selectedText, setSelectedText] = useState<string>('');
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<number | null>(null);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const transcriptRef = useRef<HTMLDivElement>(null);

  // Precompute char offsets once per segments change — O(n).
  // Without this, getAnnotationHighlights below is O(n²) per render and freezes
  // the main thread on long transcripts (>500 segments).
  // The offset rule is shared with edge-api (a notification links to the second
  // an annotation marks) — @aica/core owns it, see transcript-offset.ts.
  const charOffsets = useMemo(() => transcriptCharOffsets(segments), [segments]);

  /**
   * Позиция точки выделения в СОХРАНЁННОМ тексте транскрипта — той системе
   * координат, в которой аннотации хранятся и в которой их потом ищет
   * `getAnnotationHighlights`.
   *
   * Считается от начала СЕГМЕНТА, а не от начала панели. Прежний счёт брал
   * длину всего текста до выделения внутри `transcriptRef` — а туда попадают
   * подписи говорящих, таймкоды и проценты уверенности, которых в сохранённом
   * тексте нет вовсе. Сохранённые позиции были поэтому смещены на длину всей
   * этой обвязки, и подсветка садилась не на выделенные слова.
   *
   * Локализованный маркер вложения (#476) — вторая причина того же расхождения,
   * и она снимается тем же ходом: смещение внутри сегмента переводится из
   * показанных координат в исходные (`toSourceOffset`).
   */
  /**
   * Точка выделения в координатах СОХРАНЁННОГО текста — той системе, в которой
   * аннотации хранятся и в которой их ищет `getAnnotationHighlights`.
   *
   * Считается от начала СЕГМЕНТА, а не от начала панели: прежний счёт брал
   * длину всего текста до выделения внутри `transcriptRef`, а туда попадают
   * подписи говорящих, таймкоды и проценты уверенности, которых в сохранённом
   * тексте нет вовсе. Сохранённые позиции были смещены на длину всей этой
   * обвязки, и подсветка садилась не на выделенные слова.
   *
   * Каждый конец переводится по СВОЕМУ сегменту: выделение протягивают и через
   * границу реплик, а смещения в API — сквозные по транскрипту.
   */
  const sourcePointOf = useCallback(
    (node: Node, offsetInNode: number, side: 'start' | 'end'): number | null => {
      const element =
        node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
      const segmentEl = element?.closest('[data-segment-index]');
      const segmentIndex = Number(segmentEl?.getAttribute('data-segment-index'));
      if (!segmentEl || Number.isNaN(segmentIndex)) return null;

      const segment = segments[segmentIndex];
      if (!segment) return null;

      const upToPoint = document.createRange();
      upToPoint.setStart(segmentEl, 0);
      upToPoint.setEnd(node, offsetInNode);

      return (
        (charOffsets[segmentIndex] ?? 0) +
        toSourcePoint(segment.text, i18n.language, upToPoint.toString().length, side, {
          stripHesitations,
        })
      );
    },
    [segments, charOffsets, i18n.language, stripHesitations]
  );

  // Handle text selection
  useEffect(() => {
    const handleMouseUp = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        setSelectedText('');
        setSelectionStart(null);
        setSelectionEnd(null);
        return;
      }

      const range = selection.getRangeAt(0);
      const text = range.toString().trim();

      const startPos = sourcePointOf(range.startContainer, range.startOffset, 'start');
      const endPos = sourcePointOf(range.endContainer, range.endOffset, 'end');

      if (
        text &&
        transcriptRef.current &&
        transcriptRef.current.contains(range.commonAncestorContainer) &&
        startPos !== null &&
        endPos !== null
      ) {
        setSelectedText(text);
        setSelectionStart(startPos);
        setSelectionEnd(endPos);
      } else {
        setSelectedText('');
        setSelectionStart(null);
        setSelectionEnd(null);
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      if (selectedText && analysisId && onAnnotationCreate) {
        e.preventDefault();
        setContextMenuPos({ x: e.clientX, y: e.clientY });
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [selectedText, analysisId, onAnnotationCreate, segments, sourcePointOf]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => {
      setContextMenuPos(null);
    };

    if (contextMenuPos) {
      document.addEventListener('click', handleClick);
      return () => {
        document.removeEventListener('click', handleClick);
      };
    }
  }, [contextMenuPos]);

  // Find current segment based on playback time
  const currentSegmentIndex = segments.findIndex((seg) => {
    const startSeconds = seg.start / 1000;
    const endSeconds = seg.end / 1000;
    return currentTime >= startSeconds && currentTime < endSeconds;
  });

  // Auto-scroll to current segment
  useEffect(() => {
    if (activeSegmentRef.current && currentSegmentIndex >= 0) {
      activeSegmentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentSegmentIndex]);

  useEffect(() => {
    if (!quoteKey) return;
    transcriptRef.current
      ?.querySelector('[data-objection-quote="true"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [quoteKey]);

  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const speakerNames = useMemo(
    () =>
      speakerNamesFor(
        segments.map((segment) => segment.speaker),
        { speakerRoles, agentId, agentName, participantName }
      ),
    [segments, speakerRoles, agentId, agentName, participantName]
  );
  const getSpeakerLabel = (speaker: string) => speakerLabel(speaker, speakerNames, t);

  const getSpeakerColor = (speaker: string) => {
    const side = speakerSide(speaker, speakerRoles);
    if (side === 'agent') return { text: 'text-primary', dot: 'bg-primary' };
    if (side === 'client') return { text: 'text-accent', dot: 'bg-accent' };
    return { text: 'text-fg-subtle', dot: 'bg-fg-subtle' };
  };

  // Get annotation highlights for a segment
  const getAnnotationHighlights = (segmentText: string, segmentIndex: number) => {
    if (!annotations.length) return [];

    const charOffset = charOffsets[segmentIndex] ?? 0;

    return (
      annotations
        .filter((ann) => {
          if (ann.startPosition === null || ann.endPosition === null) return false;
          const annStart = ann.startPosition;
          const annEnd = ann.endPosition;
          const segStart = charOffset;
          const segEnd = charOffset + segmentText.length;
          // Check if annotation overlaps with this segment
          return annStart < segEnd && annEnd > segStart;
        })
        .map((ann) => {
          const rawStart = Math.max(0, (ann.startPosition || 0) - charOffset);
          const rawEnd = Math.min(
            segmentText.length,
            (ann.endPosition || segmentText.length) - charOffset
          );
          // Граница, попавшая внутрь маркера, раздвигается до его краёв. Иначе
          // нарезка разрежет маркер пополам, и ни одна часть не будет содержать
          // его целиком — `show()` не узнает форму, и наружу полезут куски
          // машинного токена. Аннотации, созданные до локализации, вполне могли
          // такую границу получить.
          const { start, end } = expandRangeOverMarkers(segmentText, rawStart, rawEnd);
          return {
            annotation: ann,
            startInSegment: start,
            endInSegment: end,
            rawStart,
            rawEnd,
          };
        })
        // Раздвижка может свести две аннотации, чьи границы лежали внутри
        // одного маркера, к одному и тому же отрезку. `SegmentText` перекрытий
        // не сливает: для каждой подсветки он выводит свой `substring` — и
        // маркер отрисовался бы дважды.
        //
        // Снимается ТОЛЬКО перекрытие, которого в исходных границах не было,
        // то есть созданное самой раздвижкой. Аннотации, перекрывавшиеся и до
        // неё, проходят как проходили: их рендер — отдельная беда, не этой
        // задачи, и молча терять их здесь нельзя.
        //
        // Подрезка не разрежет маркер: все концы уже стоят на его краях либо
        // вне его. Аннотацию, целиком лежащую внутри маркера, при этом видно
        // не будет — плоский рендер двух подсветок на одних символах не умеет,
        // а дублирование текста хуже.
        .sort((a, b) => a.startInSegment - b.startInSegment)
        .reduce<
          Array<{
            annotation: Annotation;
            startInSegment: number;
            endInSegment: number;
            rawStart: number;
            rawEnd: number;
          }>
        >((kept, highlight) => {
          const prev = kept[kept.length - 1];
          const overlapIsFromExpansion =
            prev !== undefined &&
            highlight.startInSegment < prev.endInSegment &&
            // сравнение по ИСХОДНЫМ границам: раздвижка тянет начало только
            // влево, поэтому «раньше не пересекались» видно именно здесь
            highlight.rawStart >= prev.rawEnd;

          if (!overlapIsFromExpansion) {
            kept.push(highlight);
            return kept;
          }

          const startInSegment = prev.endInSegment;
          if (startInSegment >= highlight.endInSegment) return kept;
          kept.push({ ...highlight, startInSegment });
          return kept;
        }, [])
    );
  };

  const handleAnnotationClick = () => {
    if (
      selectedText &&
      selectionStart !== null &&
      selectionEnd !== null &&
      onAnnotationCreate
    ) {
      onAnnotationCreate(selectedText, selectionStart, selectionEnd);
      setContextMenuPos(null);
      setSelectedText('');
      setSelectionStart(null);
      setSelectionEnd(null);
      window.getSelection()?.removeAllRanges();
    }
  };

  if (segments.length === 0) {
    return (
      <div
        data-testid="transcript-viewer-empty"
        className={cn('text-sm text-fg-muted text-center py-8', className)}
      >
        <p>Транскрипт недоступний</p>
      </div>
    );
  }

  return (
    <div
      ref={transcriptRef}
      data-testid="transcript-viewer"
      className={cn('relative', className)}
    >
      {segments.map((segment, index) => {
        const isActive = index === currentSegmentIndex;
        const isQuote =
          quoteRange !== null && index >= quoteRange.from && index <= quoteRange.to;
        const label = getSpeakerLabel(segment.speaker);
        const speakerColor = getSpeakerColor(segment.speaker);
        const highlights = getAnnotationHighlights(segment.text, index);

        return (
          <div
            key={`segment-${segment.start}-${segment.speaker}`}
            data-testid={`transcript-segment-${index}`}
            ref={isActive ? activeSegmentRef : null}
            data-objection-quote={isQuote ? 'true' : undefined}
            role="button"
            tabIndex={0}
            onClick={() => onSeek?.(segment.start / 1000)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onSeek?.(segment.start / 1000);
              }
            }}
            className={cn(
              'group mb-4 cursor-pointer transition-colors',
              isQuote && 'border-l-2 border-l-warning pl-2 bg-warning-dim',
              isActive && 'border-l-2 border-l-primary pl-2 bg-primary-dim'
            )}
          >
            {/* Speaker line */}
            <div className="flex items-center gap-2 mb-1 text-xs font-medium">
              {/* Colored dot */}
              <span className={cn('w-2 h-2 rounded-full shrink-0', speakerColor.dot)} />

              {/* Speaker name */}
              <span className={speakerColor.text}>{label}</span>

              {/* Timestamp */}
              <span className="text-xs font-mono text-fg-faint">
                {formatTime(segment.start)}
              </span>

              {/* Confidence score (subtle) */}
              {segment.confidence !== undefined && (
                <span
                  className={cn(
                    'text-xs font-mono',
                    segment.confidence >= 0.9
                      ? 'text-success'
                      : segment.confidence >= 0.7
                        ? 'text-fg-faint'
                        : 'text-warning-fg'
                  )}
                >
                  {(segment.confidence * 100).toFixed(0)}%
                </span>
              )}

              {/* Pin / Play mini-buttons (hover-visible) */}
              <span className="ml-auto flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  className="p-0.5 text-micro text-fg-faint hover:text-fg transition-colors"
                  title="Pin"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Pin className="h-2.5 w-2.5" />
                </button>
                <button
                  type="button"
                  className="p-0.5 text-micro text-fg-faint hover:text-fg transition-colors"
                  title="Play"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSeek?.(segment.start / 1000);
                  }}
                >
                  <Play className="h-2.5 w-2.5" />
                </button>
              </span>
            </div>

            {/* Text with highlights */}
            <p className="text-xs leading-relaxed text-fg" data-segment-index={index}>
              <SegmentText
                text={segment.text}
                highlights={highlights}
                language={i18n.language}
                stripHesitations={stripHesitations}
              />
            </p>
          </div>
        );
      })}

      {/* Context Menu */}
      {contextMenuPos && selectedText && analysisId && onAnnotationCreate && (
        <div
          data-testid="transcript-viewer-context-menu"
          role="menu"
          className="fixed z-dropdown bg-bg-elevated border border-border rounded-card shadow-lg p-1 min-w-[200px]"
          style={{ left: contextMenuPos.x, top: contextMenuPos.y }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            data-testid="transcript-viewer-add-annotation-button"
            onClick={handleAnnotationClick}
            className="w-full text-left px-3 py-2 text-sm text-fg hover:bg-bg-surface rounded-sm transition-all duration-150"
          >
            {t('records.addAnnotation', 'Add Annotation')}
          </button>
        </div>
      )}
    </div>
  );
}
